import axios from 'axios';
import { CONFERENCE_TYPES } from '@/utils/conference-type';

const state = () => ({
  unreadCounts: {},
  title: '',
  body: '',
  personalNotifications: [],
});

const getters = {
  getUnreadCount: state => conferenceType => {
    return state.unreadCounts[conferenceType] || 0;
  },
  unreadPersonalCount: state => state.personalNotifications.filter(n => !n.readAt).length,
};

const actions = {
  async fetchUnreadCount(
    { commit, dispatch, rootState },
    conferenceType = CONFERENCE_TYPES.GLOBAL,
  ) {
    try {
      const accessToken = rootState.user.token;
      const { data } = await axios.get(
        process.env.VUE_APP_API_URL + '/announcement/get-unread-announcements-count',
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: { conferenceType },
        },
      );
      const count = typeof data === 'number' ? data : 0;
      commit('setUnreadCount', { conferenceType, count });
    } catch (e) {
      dispatch('toast/error', 'Failed to set unread count.', { root: true });
    }
  },

  async markReadAndRefetch({ commit, dispatch, rootState }, { conferenceType }) {
    const accessToken = rootState.user.token;
    try {
      await axios.request(process.env.VUE_APP_API_URL + '/announcement/mark-read-announcements', {
        method: 'post',
        headers: { Authorization: `Bearer ${accessToken}` },
        params: conferenceType !== 'Global' ? { conferenceType } : undefined,
      });
      // Everything is now read — zero the badge locally. Waiting for a refetch
      // races with the home page's parallel fetchUnreadCount, which can grab a
      // stale count before the backend processes the mark-read above.
      commit('setUnreadCount', { conferenceType, count: 0 });
      await dispatch(
        'cms/fetchAnnouncements',
        { locale: 'en', conferenceType, force: true },
        { root: true },
      );
    } catch (e) {
      console.error('Failed to mark announcements as read:', e);
      dispatch('toast/error', 'Failed to mark announcements as read.', { root: true });
    }
  },

  setNotification({ commit }, notification) {
    commit('setTitle', notification.title);
    commit('setBody', notification.body);
  },

  async loadPersonalNotifications({ commit, dispatch, rootState }) {
    try {
      const accessToken = rootState.user.token;
      const { data } = await axios.get(process.env.VUE_APP_API_URL + '/notification/personal', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      commit('setPersonalNotifications', Array.isArray(data) ? data : []);
    } catch (e) {
      dispatch('toast/error', 'Failed to load notifications.', { root: true });
    }
  },

  async markPersonalRead({ rootState }) {
    try {
      const accessToken = rootState.user.token;
      await axios.request(process.env.VUE_APP_API_URL + '/notification/personal/read', {
        method: 'patch',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    } catch (e) {
      console.error('Failed to mark notifications read:', e);
    }
  },

  async removePersonalNotification({ commit, dispatch, rootState }, id) {
    try {
      const accessToken = rootState.user.token;
      await axios.request(process.env.VUE_APP_API_URL + `/notification/personal/${id}`, {
        method: 'delete',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      commit('removePersonalNotificationById', id);
    } catch (e) {
      dispatch('toast/error', 'Failed to remove notification.', { root: true });
    }
  },
};

const mutations = {
  setUnreadCount(state, { conferenceType, count }) {
    state.unreadCounts = { ...state.unreadCounts, [conferenceType]: count };
  },
  setTitle(state, title) {
    state.title = title;
  },
  setBody(state, body) {
    state.body = body;
  },
  setPersonalNotifications(state, list) {
    state.personalNotifications = list;
  },
  removePersonalNotificationById(state, id) {
    state.personalNotifications = state.personalNotifications.filter(n => n.id !== id);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
